// dagOutcome.js — autolearn E1-WS1-T01.
// @arch:pure-engine
//
// Maps a DAG executor result (runTaskGraph → { blocked, done[], failed[], ran[], reason }) into the
// honest-green outcome shape the learning ledger consumes — the SAME signal the single-turn path records,
// which today is DROPPED at the DAG branch's early `return;` (SessionWorkspace.jsx:1306). This is the
// highest-quality signal in the system (per-task verified green/fail over a dependency-ordered graph).
//
// HONEST-GREEN (THE LAW): never fabricate green. `reachedGreen` is:
//   - false  when any task failed its oracle;
//   - true   ONLY when the graph completed (not blocked) AND every task is done AND at least one REAL
//            verifier ran (anyVerifierRan) — a graph that only "passed" via honest no-ops is NOT a
//            verified success;
//   - null   otherwise (blocked/unreachable, or all-done-but-no-real-verifier) → recorded as not_run.
//
// PURE (Tier 1 lib purity): no react, no window, no IPC.

export function mapDagResultToOutcome(dagRes, dagTasks, { anyVerifierRan = true } = {}) {
  const total = Array.isArray(dagTasks) ? dagTasks.length : 0;
  const done = Array.isArray(dagRes?.done) ? dagRes.done.length : 0;
  const failed = Array.isArray(dagRes?.failed) ? dagRes.failed.length : 0;
  const blocked = !!dagRes?.blocked;

  let reachedGreen;
  if (failed > 0) {
    reachedGreen = false;
  } else if (!blocked && total > 0 && done === total && anyVerifierRan) {
    reachedGreen = true;
  } else {
    reachedGreen = null; // blocked/unreachable OR all-done-but-no-real-verifier → not_run (honest)
  }

  const stopReason = blocked
    ? (dagRes?.reason ? `dag_${dagRes.reason}` : 'dag_blocked')
    : 'dag_done';
  const iterations = Array.isArray(dagRes?.ran) ? dagRes.ran.length : (done + failed);

  return {
    reachedGreen,
    stopReason,
    iterations,
    tasksTotal: total,
    tasksDone: done,
    tasksFailed: failed,
    surface: 'code',
  };
}
